import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * Loading placeholders that keep a screen's SHAPE while its data is on the
 * way. A grey card with a shimmer where the number will be beats an empty
 * card, a "0" that is not true, or "Loading…" in a table cell — the eye
 * already knows where to look when the value lands.
 *
 * Show these only when a query has NO data yet (`isPending`), never during a
 * background refetch — a table that flickers to grey every minute is worse
 * than one that is a minute stale.
 */

const shimmer = keyframes`
   0% { background-position: -400px 0; }
   100% { background-position: 400px 0; }
`;

export const Skeleton = styled.span<{
   $w?: string;
   $h?: string;
   $radius?: string;
   $block?: boolean;
}>`
   display: ${({ $block }) => ($block ? 'block' : 'inline-block')};
   width: ${({ $w }) => $w ?? '100%'};
   height: ${({ $h }) => $h ?? '1em'};
   border-radius: ${({ $radius }) => $radius ?? '6px'};
   background-color: ${colors.GRAY13};
   /* A pale band sliding over the theme's border colour — reads right on the
      light and the dark theme alike. */
   background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0px,
      rgba(255, 255, 255, 0.45) 40px,
      rgba(255, 255, 255, 0) 80px
   );
   background-size: 800px 100%;
   animation: ${shimmer} 1.4s linear infinite;
   vertical-align: middle;

   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;

/** A stat card's three lines: label, big number, sub-line. */
export const SkeletonStat: React.FC = () => (
   <span aria-busy="true" aria-label="Loading" style={{ display: 'block' }}>
      <Skeleton $block $w="55%" $h="10px" style={{ marginBottom: 10 }} />
      <Skeleton $block $w="70%" $h="26px" style={{ marginBottom: 8 }} />
      <Skeleton $block $w="45%" $h="10px" />
   </span>
);

/** Table rows of grey bars — one per column, `rows` deep. */
export const SkeletonRows: React.FC<{
   rows?: number;
   cols: number;
   /** The table's own cell component, so padding/borders match real rows. */
   Cell?: React.ElementType;
}> = ({ rows = 6, cols, Cell = 'td' }) => (
   <>
      {Array.from({ length: rows }, (_, r) => (
         <tr key={r} aria-busy="true">
            {Array.from({ length: cols }, (_, c) => (
               <Cell key={c}>
                  <Skeleton $h="12px" $w={c === 0 ? '80%' : '60%'} />
               </Cell>
            ))}
         </tr>
      ))}
   </>
);

/** A chart-sized grey area. */
export const SkeletonChart: React.FC<{ height?: number }> = ({ height = 220 }) => (
   <Skeleton $block $h={`${height}px`} $radius="10px" aria-busy="true" aria-label="Loading chart" />
);

/** Stacked text lines for a list or card body. */
export const SkeletonLines: React.FC<{ lines?: number }> = ({ lines = 4 }) => (
   <span aria-busy="true" aria-label="Loading" style={{ display: 'block' }}>
      {Array.from({ length: lines }, (_, i) => (
         <Skeleton
            key={i}
            $block
            $h="12px"
            $w={`${[90, 70, 80, 60, 75, 85][i % 6]}%`}
            style={{ marginBottom: 10 }}
         />
      ))}
   </span>
);

export default Skeleton;
